// shell/panes/entity-pane.ts
// Entity pane factory for CRUD entity views

import type {
    PaneType,
    PaneInstance,
    PaneTypeRenderContext,
    ActionLocation,
    ActionContext,
} from '../core/types';
import { ActionRegistry } from '../core/action-registry';
import { createElement } from '../utils/dom';
import { EntityView } from '../components/entity-view';
import { renderPaneActions } from './pane-actions';
import { planEntityPaneActions, canEdit, canDelete } from './entity-pane-actions';
import type {
    EntityViewConfig,
    EntityViewMode,
    ViewConfig,
    FormConfig,
} from '../components/entity-view/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Action context passed to entity action handlers.
 */
export interface EntityActionContext<T> {
    instance: PaneInstance;
    shell: PaneTypeRenderContext['shell'];
    entityView: EntityView<T>;
    mode: EntityViewMode;
}

/**
 * Entity-specific action definition.
 */
export interface EntityAction<T> {
    /** Action identifier (scoped to pane type) */
    key: string;
    /** Display label */
    label: string;
    /** Optional icon */
    icon?: string;
    /** Which modes this action appears in (default: all) */
    modes?: EntityViewMode[];
    /** Action locations (default: ['toolbar']) */
    locations?: ActionLocation[];
    /** Keyboard shortcut */
    shortcut?: string;
    /** Visual variant */
    variant?: 'default' | 'primary' | 'danger';
    /**
     * Title-row placement only: fold this action into the pane's trailing ⋯
     * menu instead of rendering an inline button. Ignored in the toolbar.
     */
    overflow?: boolean;
    /**
     * Whether the action applies to this model at all. When it returns false
     * the action is omitted from the plan (title-row placement) or hidden from
     * the toolbar — use it for mutually exclusive verbs (Publish vs Unpublish)
     * so the row does not fill with permanently greyed buttons. `canExecute`
     * is the other axis: the verb applies but cannot run right now, so it
     * renders disabled with `disabledReason`. Default: visible.
     */
    visible?: (model: T, ctx: EntityActionContext<T>) => boolean;
    /** Whether action can execute (receives current model) */
    canExecute?: (model: T, ctx: EntityActionContext<T>) => boolean;
    /**
     * Title-row placement only: tooltip shown while `canExecute` is false,
     * so a disabled button says why.
     */
    disabledReason?: (model: T, ctx: EntityActionContext<T>) => string | undefined;
    /** Execute the action */
    execute: (model: T, ctx: EntityActionContext<T>) => void | Promise<void>;
}

/**
 * How the built-in Delete presents itself. Every field is optional and every
 * default is what the pane did before this existed: an inline "Delete" button,
 * a "Delete" confirm dialog with a "Delete" confirm button, dismissable, and
 * the pane closed once the delete succeeds.
 */
export interface EntityDeleteConfig {
    /** Button label (default "Delete"). The key stays `delete`, the variant `danger`. */
    label?: string;
    /** Title-row placement: fold Delete into the trailing ⋯ menu (default false: inline). */
    overflow?: boolean;
    /** Confirm dialog title (default "Delete"). */
    confirmTitle?: string;
    /** Confirm button label (default "Delete"). */
    confirmLabel?: string;
    /** Require an explicit yes/no — no overlay or Escape dismissal (default false). */
    requireExplicitChoice?: boolean;
    /**
     * Close the pane once the delete succeeds (default true). Set false when
     * the record survives the verb (a revoke that leaves the record on screen
     * in its new state).
     */
    closePane?: boolean;
}

/**
 * Configuration for createEntityPaneType factory.
 */
export interface EntityPaneConfig<T> {
    /** Unique pane type identifier */
    typeId: string;
    /** Default title for new instances */
    defaultTitle: string;

    // -------------------------------------------------------------------------
    // Data
    // -------------------------------------------------------------------------

    /** Async function to load the entity model */
    getModel: (ctx: PaneTypeRenderContext) => Promise<T>;

    // -------------------------------------------------------------------------
    // View Configuration
    // -------------------------------------------------------------------------

    /**
     * View mode configuration. Function form receives ctx, mirroring `form`
     * and `newForm` — the way a view field that has to reach the render
     * context (a custom renderer that opens a sibling pane, a label derived
     * from the pane's own context) gets hold of it. Resolved once per render.
     */
    view: ViewConfig<T> | ((ctx: PaneTypeRenderContext) => ViewConfig<T>);

    /** Edit mode configuration. Optional for view-only entities. Function form receives ctx for dynamic fields. */
    form?: FormConfig<T> | ((ctx: PaneTypeRenderContext) => FormConfig<T>);

    /** New mode configuration (defaults to form if not provided). Function form receives ctx for dynamic fields. */
    newForm?: FormConfig<T> | ((ctx: PaneTypeRenderContext) => FormConfig<T>);

    /**
     * Form whose field definitions depend on the loaded model (e.g. select
     * options fetched together with it). Evaluated after every load, before
     * the first form render, and takes precedence over `form`. `form` may then
     * be omitted; keep `newForm` for a differing new-mode layout.
     */
    buildForm?: (model: T, ctx: PaneTypeRenderContext) => FormConfig<T>;

    // -------------------------------------------------------------------------
    // CRUD
    // -------------------------------------------------------------------------

    /** Whether edit is allowed (boolean or predicate) */
    canEdit?: boolean | ((model: T) => boolean);

    /** Whether delete is allowed (boolean or predicate) */
    canDelete?: boolean | ((model: T) => boolean);

    /** Called when saving changes to existing entity */
    onUpdate?: (model: T, ctx: PaneTypeRenderContext) => Promise<void>;

    /** Called when creating new entity */
    onCreate?: (model: T, ctx: PaneTypeRenderContext) => Promise<void>;

    /** Called when deleting entity */
    onDelete?: (ctx: PaneTypeRenderContext) => Promise<void>;

    // -------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------

    /** Custom entity-specific actions */
    actions?: EntityAction<T>[];

    /**
     * Where the pane's actions render.
     * - `'toolbar'` (default): nothing is rendered by the pane itself; call
     *   `registerEntityPaneActions` to put Edit/Delete/custom actions in the
     *   shell's global action bar for the focused pane. Save/Cancel stay in
     *   the in-body form header.
     * - `'pane'`: Edit, Delete, custom actions AND the edit/new-mode commit pair
     *   render in the pane's own title row via `tabBarActions`, re-planned on
     *   load, mode change, dirty change and after each action. The in-body
     *   form header is suppressed. `registerEntityPaneActions` is a no-op.
     */
    actionsPlacement?: 'toolbar' | 'pane';

    /**
     * Title-row placement: label of the commit button (default "Save", or
     * "Create" in new mode). A function receives the mode.
     */
    submitLabel?: string | ((mode: 'edit' | 'new') => string);

    /** Title-row placement: label of the cancel button (default "Cancel"). */
    cancelLabel?: string;

    /**
     * Title-row placement: veto the commit. Return a reason to disable the
     * commit button with that reason as its tooltip, or `undefined` to allow.
     * Re-evaluated on every re-plan — call `shell.refreshPaneActions(id)`
     * when the underlying condition changes.
     */
    submitBlockedReason?: (model: T, ctx: EntityActionContext<T>) => string | undefined;

    // -------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------

    /** Called after model is loaded */
    afterLoad?: (ctx: PaneTypeRenderContext) => Promise<void>;

    /**
     * Called when Cancel is clicked in edit/new mode.
     * If provided, prevents default view-mode switch (e.g., to close the pane instead).
     */
    onCancel?: (ctx: PaneTypeRenderContext) => void;

    /** Called when mode changes (view/edit/new) */
    onModeChange?: (mode: EntityViewMode, ctx: PaneTypeRenderContext) => void;

    /**
     * Called when a form field's value changes in edit/new mode (mirrors the
     * EntityView hook, plus `ctx` so the consumer can reach the shell / open a
     * dialog / react to a field — e.g. probe a typed path and offer a grant).
     */
    onFieldChange?: (key: string, value: unknown, ctx: PaneTypeRenderContext) => void;

    /** Called when pane is destroyed. Runs before EntityView disposal. */
    onDestroy?: (instance: PaneInstance) => void;

    /**
     * Element mounted once above the entity view, outside its render cycle —
     * a notice or readiness banner the view's re-renders must not wipe. The
     * pane owns it: update it from your own subscriptions (via
     * `ctx.disposables`). Return null/undefined for none.
     */
    renderBanner?: (ctx: PaneTypeRenderContext) => HTMLElement | null | undefined;

    // -------------------------------------------------------------------------
    // Initial Mode
    // -------------------------------------------------------------------------

    /**
     * Initial mode for the entity view.
     * - Static value: always start in this mode
     * - Function: compute mode from context (e.g., 'new' when no ID in context)
     * - Omitted: reads `context.mode`, falling back to `'view'`
     */
    initialMode?: EntityViewMode | ((ctx: PaneTypeRenderContext) => EntityViewMode);

    // -------------------------------------------------------------------------
    // Pane Settings
    // -------------------------------------------------------------------------

    /** Additional capabilities (pane:{typeId} is auto-added) */
    capabilities?: string[];

    /** Default pane instance flags */
    defaultFlags?: Partial<PaneInstance['flags']>;

    /**
     * Flow-surface sizing, passed straight through to the pane type: preferred
     * width in pixels (the surface's own default applies when omitted).
     */
    defaultWidth?: number;

    /** Flow surfaces: may this pane shrink below its ideal width? (default true) */
    canShrink?: boolean;

    /** Flow surfaces: does this pane absorb the remaining width? (default false) */
    stretch?: boolean;

    /** Dirty state confirmation (default: true) */
    confirmDirtyState?: boolean;

    /** Hide the form header (Save/Cancel buttons). Default: false */
    hideFormHeader?: boolean;

    /**
     * Make a display-only view editable from within the pane: renders an
     * in-pane "Edit" button (and, when `canDelete`, a "Delete" button) in a
     * header above the view content — the same place the form header sits —
     * instead of relying on toolbar/global actions. Use this INSTEAD of
     * `registerEntityPaneActions` for self-contained CRUD panes. In this mode
     * delete lives in the view header (gated by `canDelete`); the edit form
     * header shows only Save/Cancel. Default: false. Ignored when
     * `actionsPlacement` is `'pane'` (the title row already carries them).
     */
    editableInView?: boolean;

    /**
     * Delete confirmation message, asked through `shell.confirm` by the built-in
     * Delete (title row, view header and toolbar variants alike).
     * - string: custom confirm message
     * - false: no built-in confirm (consumer handles in onDelete)
     * - undefined: default message ('Are you sure you want to delete this item?')
     */
    deleteConfirmMessage?: string | false;

    /**
     * How the built-in Delete presents itself: its label, whether it folds into
     * the ⋯ menu, what its confirm dialog says, and whether the pane closes
     * afterwards. Omit for today's behaviour. `deleteConfirmMessage` still owns
     * the confirm MESSAGE (and `false` there still opts out of both the
     * built-in confirm and the close, handing them to `onDelete`).
     */
    delete?: EntityDeleteConfig;

    /** ARIA label (string or function) */
    ariaLabel: string | ((instance: PaneInstance) => string);

    /**
     * If set, the entity pane subscribes to `data-changed` shell events with
     * matching type (any of them, when given a list) and auto-reloads the
     * model when in view mode.
     * Use for entity panes that should reflect server-side changes in real time.
     */
    refreshOnDataChanged?: string | string[];
}

// ============================================================================
// Instance State (WeakMap for privacy)
// ============================================================================

// Store EntityView instances keyed by PaneInstance
const entityViews = new WeakMap<PaneInstance, EntityView<unknown>>();

// Render contexts, for `tabBarActions` (which only receives the instance).
const renderContexts = new WeakMap<PaneInstance, PaneTypeRenderContext>();

/**
 * Get the EntityView for a pane instance.
 * Useful for action handlers.
 */
export function getEntityView<T>(instance: PaneInstance): EntityView<T> | undefined {
    return entityViews.get(instance) as EntityView<T> | undefined;
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Creates an entity pane type for viewing and editing entities.
 *
 * @example
 * ```typescript
 * const personPane = createEntityPaneType<Person>({
 *     typeId: 'person-detail',
 *     defaultTitle: 'Person',
 *     getModel: (ctx) => api.getPerson(ctx.instance.context.personId),
 *     view: {
 *         fields: [...],
 *         layout: [...]
 *     },
 *     form: {
 *         fields: [...],
 *         layout: [...]
 *     },
 *     canEdit: true,
 *     canDelete: (model) => model.role !== 'admin',
 *     onUpdate: (model) => api.updatePerson(model),
 *     onDelete: (ctx) => api.deletePerson(ctx.instance.context.personId),
 *     ariaLabel: 'Person details'
 * });
 * ```
 */
export function createEntityPaneType<T>(config: EntityPaneConfig<T>): PaneType {
    // Derive capabilities
    const capabilities = [
        `pane:${config.typeId}`,
        ...(config.capabilities ?? []),
    ];

    const inPane = config.actionsPlacement === 'pane';
    const hasForm = !!(config.form ?? config.buildForm);

    return {
        typeId: config.typeId,
        kind: 'leaf',
        defaultTitle: config.defaultTitle,
        capabilities,
        defaultFlags: config.defaultFlags,
        defaultWidth: config.defaultWidth,
        canShrink: config.canShrink,
        stretch: config.stretch,

        tabBarActions: inPane ? (instance) => buildPaneActions(instance, config, hasForm) : undefined,

        render: (ctx: PaneTypeRenderContext): HTMLElement => {
            const container = createElement('div', {
                className: 'cs-entity-pane',
            });
            renderContexts.set(ctx.instance, ctx);
            const refreshPaneActions = () => {
                if (inPane) ctx.shell.refreshPaneActions(ctx.instance.instanceId);
            };

            // A banner the pane owns, mounted before the EntityView appends its
            // own root so it sits above the view and survives its re-renders.
            const banner = config.renderBanner?.(ctx);
            if (banner) container.appendChild(banner);

            // Determine initial mode
            const initialMode = typeof config.initialMode === 'function'
                ? config.initialMode(ctx)
                : config.initialMode ?? (ctx.instance.context.mode as EntityViewMode) ?? 'view';

            // Resolve aria label
            const ariaLabel = typeof config.ariaLabel === 'function'
                ? config.ariaLabel(ctx.instance)
                : config.ariaLabel;

            // Resolve view/form configs (may be functions needing ctx)
            const resolvedView = typeof config.view === 'function'
                ? config.view(ctx) : config.view;
            const resolvedForm = typeof config.form === 'function'
                ? config.form(ctx) : config.form;
            const resolvedNewForm = typeof config.newForm === 'function'
                ? config.newForm(ctx) : config.newForm;

            // Declared before viewConfig so the `viewActions` closure (which
            // drives the in-pane Edit/Delete buttons) can reach the live view.
            let entityView: EntityView<T>;
            let lastDirty = false;
            let lastBlocked: string | undefined;

            /** The live commit veto, or undefined when none is configured. */
            const blockedReason = (): string | undefined => {
                if (!config.submitBlockedReason || !entityView?.model) return undefined;
                return config.submitBlockedReason(
                    entityView.model,
                    buildEntityActionContext(ctx, entityView),
                );
            };

            // Create EntityView config
            const viewConfig: EntityViewConfig<T> = {
                container,
                getModel: () => config.getModel(ctx),
                initialMode,
                view: resolvedView,
                form: resolvedForm,
                newForm: resolvedNewForm,
                onUpdate: config.onUpdate
                    ? (model) => config.onUpdate!(model, ctx)
                    : undefined,
                onCreate: config.onCreate
                    ? (model) => config.onCreate!(model, ctx)
                    : undefined,
                // When the title row (pane placement) or the view header
                // (editableInView) owns Delete, the edit form header omits it —
                // don't hand onDelete to the EntityView form header.
                onDelete: inPane || config.editableInView
                    ? undefined
                    : config.onDelete
                        ? () => config.onDelete!(ctx)
                        : undefined,
                afterLoad: async () => {
                    // Field definitions that depend on the model: EntityView reads
                    // its form config lazily at render time, and this runs before
                    // the post-load render, so swapping it in here is enough.
                    if (config.buildForm && entityView.model !== null) {
                        viewConfig.form = config.buildForm(entityView.model, ctx);
                    }
                    await config.afterLoad?.(ctx);
                    // Model is now available — refresh action bar so isEnabled checks see it
                    ctx.shell.refreshActionBar();
                    lastDirty = entityView.isDirty;
                    lastBlocked = blockedReason();
                    refreshPaneActions();
                },
                onCancel: config.onCancel
                    ? () => config.onCancel!(ctx)
                    : undefined,
                onNewCancel: () => ctx.surface?.closePane(ctx.instance.instanceId),
                onModeChange: (mode) => {
                    config.onModeChange?.(mode, ctx);
                    lastDirty = entityView.isDirty;
                    lastBlocked = blockedReason();
                    refreshPaneActions();
                },
                onFieldChange: (key, value) => {
                    config.onFieldChange?.(key, value, ctx);
                    if (!inPane) return;
                    // Two things a field change can move, and either one has to
                    // re-plan the row:
                    //  - dirtiness, which gates the commit in EDIT mode;
                    //  - `submitBlockedReason`, which gates it in edit AND new
                    //    mode — new mode has no dirty transition to ride on, so
                    //    gating on dirtiness alone left the commit stuck at
                    //    whatever the empty form said.
                    const dirtyMoved = entityView.mode === 'edit' && entityView.isDirty !== lastDirty;
                    const blocked = blockedReason();
                    const blockedMoved = blocked !== lastBlocked;
                    if (!dirtyMoved && !blockedMoved) return;
                    lastDirty = entityView.isDirty;
                    lastBlocked = blocked;
                    refreshPaneActions();
                },
                confirmDirtyState: config.confirmDirtyState,
                confirmDiscard: () => ctx.shell.confirm({
                    title: 'Unsaved changes',
                    message: 'You have unsaved changes. Discard them?',
                    confirmLabel: 'Discard',
                    confirmVariant: 'no',
                }),
                hideFormHeader: inPane ? true : config.hideFormHeader,
                // In-pane Edit/Delete buttons for self-contained CRUD panes.
                viewActions: !inPane && config.editableInView
                    ? () => buildEditableViewActions(entityView, config, ctx, !!resolvedForm)
                    : undefined,
                ariaLabel,
            };

            // Create EntityView
            entityView = new EntityView(viewConfig);

            // Store for action access and register with disposables for auto-cleanup
            entityViews.set(ctx.instance, entityView as EntityView<unknown>);
            ctx.disposables.add(entityView);

            // Auto-reload on data-changed events (e.g., WebSocket push)
            if (config.refreshOnDataChanged) {
                const dataTypes = Array.isArray(config.refreshOnDataChanged)
                    ? config.refreshOnDataChanged
                    : [config.refreshOnDataChanged];
                ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
                    const type = (data as { type?: string })?.type;
                    if (type !== undefined && dataTypes.includes(type) && entityView.mode === 'view') {
                        entityView.reload();
                    }
                }));
            }

            return container;
        },

        onDestroy: (instance: PaneInstance): void => {
            // App-level cleanup
            config.onDestroy?.(instance);

            // EntityView is auto-disposed via ctx.disposables, but clean up the WeakMaps
            entityViews.delete(instance);
            renderContexts.delete(instance);
        },
    };
}

/**
 * Title-row actions for a pane-placement entity pane: plan from the live
 * EntityView, render with the shared renderer. Empty until the model loads.
 */
function buildPaneActions<T>(
    instance: PaneInstance,
    config: EntityPaneConfig<T>,
    hasForm: boolean,
): HTMLElement[] {
    const ev = entityViews.get(instance) as EntityView<T> | undefined;
    const ctx = renderContexts.get(instance);
    if (!ev || !ctx) return [];
    const actionCtx = buildEntityActionContext(ctx, ev);
    const refresh = () => ctx.shell.refreshPaneActions(instance.instanceId);
    const plan = planEntityPaneActions(config, {
        mode: ev.mode,
        model: ev.model,
        hasForm,
        isDirty: ev.isDirty,
    }, actionCtx, {
        edit: () => { void ev.setMode('edit'); },
        save: () => { void ev.save(); },
        cancel: () => ev.cancel(),
        delete: () => { void deleteFromPane(config, ctx); },
        // Actions usually change what is allowed next; re-plan once they settle.
        run: (action) => {
            if (!ev.model) return;
            void Promise.resolve(action.execute(ev.model, actionCtx)).finally(refresh);
        },
    });
    return renderPaneActions(plan);
}

/**
 * The built-in Delete confirmation, through the shell's modal (rule 6: never
 * `window.confirm`). Callers skip it when `deleteConfirmMessage === false`.
 */
function confirmDelete<T>(shell: PaneTypeRenderContext['shell'], config: EntityPaneConfig<T>): Promise<boolean> {
    const message = typeof config.deleteConfirmMessage === 'string'
        ? config.deleteConfirmMessage
        : 'Are you sure you want to delete this item?';
    return shell.confirm({
        title: config.delete?.confirmTitle ?? 'Delete',
        message,
        confirmLabel: config.delete?.confirmLabel ?? 'Delete',
        confirmVariant: 'no',
        requireExplicitChoice: config.delete?.requireExplicitChoice,
    });
}

/** Does a successful built-in delete close the pane? (default: yes) */
function closesOnDelete<T>(config: EntityPaneConfig<T>): boolean {
    return config.delete?.closePane !== false;
}

/**
 * Delete from the title row: same confirm/close contract as the toolbar and
 * `editableInView` variants — `deleteConfirmMessage === false` hands confirm
 * AND close to `onDelete`, otherwise `shell.confirm` then auto-close.
 */
async function deleteFromPane<T>(config: EntityPaneConfig<T>, ctx: PaneTypeRenderContext): Promise<void> {
    if (!config.onDelete) return;
    if (config.deleteConfirmMessage === false) {
        await config.onDelete(ctx);
        return;
    }
    if (!(await confirmDelete(ctx.shell, config))) return;
    await config.onDelete(ctx);
    if (closesOnDelete(config)) ctx.surface?.closePane(ctx.instance.instanceId);
}

// ============================================================================
// Helper Functions
// ============================================================================

// `canEdit` / `canDelete` live with the planner (pure, node-testable).
export { canEdit, canDelete };

/**
 * Build action context for entity actions.
 */
export function buildEntityActionContext<T>(
    ctx: PaneTypeRenderContext,
    entityView: EntityView<T>
): EntityActionContext<T> {
    return {
        instance: ctx.instance,
        shell: ctx.shell,
        entityView,
        mode: entityView.mode,
    };
}

/**
 * Build the in-pane view-header buttons for an `editableInView` pane: an "Edit"
 * button (when a form is configured and `canEdit` passes) and a "Delete" button
 * (when `onDelete` is set and `canDelete` passes). Re-evaluated on every view
 * render, so per-model gating tracks the live model. Mirrors the delete
 * confirm/close semantics of `registerEntityPaneActions`.
 */
function buildEditableViewActions<T>(
    view: EntityView<T>,
    config: EntityPaneConfig<T>,
    ctx: PaneTypeRenderContext,
    hasForm: boolean
): HTMLElement[] {
    const model = view.model;
    if (!model) return [];
    const out: HTMLElement[] = [];

    if (hasForm && canEdit(config, model)) {
        out.push(createElement('button', {
            className: 'cs-btn',
            textContent: 'Edit',
            attributes: { type: 'button', title: 'Edit' },
            onClick: () => { void view.setMode('edit'); },
        }));
    }

    if (config.onDelete && canDelete(config, model)) {
        const label = config.delete?.label ?? 'Delete';
        out.push(createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: label,
            attributes: { type: 'button', title: label },
            onClick: async () => {
                // deleteConfirmMessage === false ⇒ onDelete owns confirm + close
                // (so a cancelled confirm doesn't close the pane). Otherwise use
                // the built-in shell.confirm and auto-close on success.
                if (config.deleteConfirmMessage === false) {
                    await config.onDelete!(ctx);
                    return;
                }
                if (!(await confirmDelete(ctx.shell, config))) return;
                await config.onDelete!(ctx);
                if (closesOnDelete(config)) ctx.surface?.closePane(ctx.instance.instanceId);
            },
        }));
    }

    return out;
}

// ============================================================================
// Action Registration
// ============================================================================

/**
 * Register entity pane actions with the ActionRegistry.
 * Call this after registering the pane type with the shell.
 *
 * @example
 * ```typescript
 * const personPane = createEntityPaneType<Person>({ ... });
 * shell.registerPaneType(personPane);
 * registerEntityPaneActions(actionRegistry, personPaneConfig);
 * ```
 */
export function registerEntityPaneActions<T>(
    registry: ActionRegistry,
    config: EntityPaneConfig<T>
): void {
    const typeId = config.typeId;
    if (config.actionsPlacement === 'pane') {
        console.warn(`registerEntityPaneActions('${typeId}'): actionsPlacement is 'pane' — its actions `
            + 'already render in the pane title row; nothing registered in the action bar.');
        return;
    }
    const capability = `pane:${typeId}`;

    // Register Edit action if canEdit is configured
    if (config.canEdit !== undefined) {
        registry.register({
            actionId: `${typeId}.edit`,
            label: 'Edit',
            icon: 'edit',
            locations: ['toolbar', 'contextmenu'],
            requiredCapabilities: [capability],
            contextPredicate: (ctx: ActionContext) => {
                const ev = getEntityView(ctx.focusedPane!);
                return ev?.mode === 'view';
            },
            isEnabled: (ctx: ActionContext) => {
                const ev = getEntityView<T>(ctx.focusedPane!);
                if (!ev?.model) return false;
                return canEdit(config, ev.model);
            },
            execute: async (ctx: ActionContext) => {
                const ev = getEntityView(ctx.focusedPane!);
                await ev?.setMode('edit');
            },
        });
    }

    // Register Delete action if canDelete is configured
    if (config.canDelete !== undefined && config.onDelete) {
        registry.register({
            actionId: `${typeId}.delete`,
            label: config.delete?.label ?? 'Delete',
            icon: 'trash',
            locations: ['toolbar', 'contextmenu'],
            requiredCapabilities: [capability],
            contextPredicate: (ctx: ActionContext) => {
                const ev = getEntityView(ctx.focusedPane!);
                return ev?.mode === 'view';
            },
            isEnabled: (ctx: ActionContext) => {
                const ev = getEntityView<T>(ctx.focusedPane!);
                if (!ev?.model) return false;
                return canDelete(config, ev.model);
            },
            execute: async (ctx: ActionContext) => {
                // Confirm deletion (unless disabled)
                if (config.deleteConfirmMessage !== false) {
                    if (!(await confirmDelete(ctx.shell, config))) return;
                }

                const ev = getEntityView<T>(ctx.focusedPane!);
                if (!ev) return;

                // Build a render context for onDelete (reuses existing disposables)
                const renderCtx = ctx.shell.createRenderContext(ctx.focusedPane!);

                await config.onDelete!(renderCtx);

                // Close the pane after deletion, unless the record survives it.
                if (!closesOnDelete(config)) return;
                const surface = ctx.shell.getSurfaceForPane(ctx.focusedPane!.instanceId);
                surface?.closePane(ctx.focusedPane!.instanceId);
            },
        });
    }

    // Register custom actions
    if (config.actions) {
        for (const action of config.actions) {
            registry.register({
                actionId: `${typeId}.${action.key}`,
                label: action.label,
                icon: action.icon,
                shortcut: action.shortcut,
                locations: action.locations ?? ['toolbar'],
                requiredCapabilities: [capability],
                contextPredicate: (ctx: ActionContext) => {
                    const ev = getEntityView<T>(ctx.focusedPane!);
                    if (action.modes && !(ev ? action.modes.includes(ev.mode) : false)) return false;
                    if (action.visible && ev?.model) {
                        return action.visible(ev.model, { instance: ctx.focusedPane!, shell: ctx.shell, entityView: ev, mode: ev.mode });
                    }
                    return true;
                },
                isEnabled: (ctx: ActionContext) => {
                    if (!action.canExecute) return true;
                    const ev = getEntityView<T>(ctx.focusedPane!);
                    if (!ev?.model) return false;

                    const actionCtx: EntityActionContext<T> = {
                        instance: ctx.focusedPane!,
                        shell: ctx.shell,
                        entityView: ev,
                        mode: ev.mode,
                    };
                    return action.canExecute(ev.model, actionCtx);
                },
                execute: async (ctx: ActionContext) => {
                    const ev = getEntityView<T>(ctx.focusedPane!);
                    if (!ev?.model) return;

                    const actionCtx: EntityActionContext<T> = {
                        instance: ctx.focusedPane!,
                        shell: ctx.shell,
                        entityView: ev,
                        mode: ev.mode,
                    };
                    await action.execute(ev.model, actionCtx);
                },
            });
        }
    }
}

/**
 * Unregister all entity pane actions from the ActionRegistry.
 */
export function unregisterEntityPaneActions<T>(
    registry: ActionRegistry,
    config: EntityPaneConfig<T>
): void {
    const typeId = config.typeId;

    // Unregister built-in actions
    registry.unregister(`${typeId}.edit`);
    registry.unregister(`${typeId}.delete`);

    // Unregister custom actions
    if (config.actions) {
        for (const action of config.actions) {
            registry.unregister(`${typeId}.${action.key}`);
        }
    }
}
